import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ItemList from '../../Common/ItemList/ItemList'
import { Box, Divider, Paper, Stack, Typography } from '@mui/material'
import AccountHeader from '../AccountHeader'
import { type CartStatus } from '../../UserData/_stores/UserCartStore'
import type UserCartStore from '../../UserData/_stores/UserCartStore'
import { LIST_CART_STATUSES } from '../../UserData/_stores/UserCartStore'
import OrderRow from './OrderRow'
import SearchBar from '../../Common/ItemList/SearchBar'
import { useSearchParams } from 'react-router'
import OrderRowSkeleton from './OrderRowSkeleton'
import AccountFilterDrawer from '../Filters/AccountFilterDrawer'
import AccountFilterSection from '../Filters/AccountFilterSection'
import AccountLimitFilter from '../Filters/AccountLimitFilter'
import AccountSortFilter, { type AccountDateSortType, type AccountSortOption } from '../Filters/AccountSortFilter'
import AccountDateRangeFilter, { type AccountDateRangePreset, type AccountDateRangeValue } from '../Filters/AccountDateRangeFilter'
import OrderStatusFilter from './OrderStatusFilter'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import AccountNoResults from '../AccountNoResults'

const SEARCH_FIELDS = ['search', 'statuses', 'sort', 'dateRange', 'dateFrom', 'dateTo']
const LIST_STATUS_FILTERS = LIST_CART_STATUSES.filter(value => !['cart'].includes(value))
const DEFAULT_STATUS_FILTERS = LIST_CART_STATUSES.filter(value => !['cart', 'ignored'].includes(value))
const SORT_OPTIONS: AccountDateSortType[] = ['n-to-o', 'o-to-n']
const DATE_RANGE_OPTIONS: AccountDateRangePreset[] = ['all', 'today', 'last-7-days', 'last-30-days', 'this-month', 'last-month', 'custom']

const isDefaultStatusFilter = (statuses: CartStatus[]) => {
  return statuses.length === DEFAULT_STATUS_FILTERS.length &&
    DEFAULT_STATUS_FILTERS.every(status => statuses.includes(status))
}

const isDateRangePreset = (value: string | null): value is AccountDateRangePreset => {
  return !!value && DATE_RANGE_OPTIONS.includes(value as AccountDateRangePreset)
}

export default function OrderList() {
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')
  const [searchParams] = useSearchParams()
  const userLimit = useAppSelector(state => state.get('userData').get('prefs').get('accountPageLimit'))

  const paramSearch = searchParams.get('search')
  const paramStatuses = searchParams.get('statuses')
  const paramDateFrom = searchParams.get('dateFrom') ?? ''
  const paramDateTo = searchParams.get('dateTo') ?? ''
  const paramDateRange = searchParams.get('dateRange')
  const sortParam = searchParams.get('sort')
  const dateRange = isDateRangePreset(paramDateRange)
    ? paramDateRange
    : (paramDateFrom || paramDateTo ? 'custom' : 'all')
  const sort = sortParam
    ? sortParam.split(',').filter((value): value is AccountDateSortType => SORT_OPTIONS.includes(value as AccountDateSortType))
    : []
  const [search, setSearch] = useState({
    search: paramSearch ? paramSearch.split(',') : [],
    statuses: paramStatuses ? paramStatuses.split(',').filter((value): value is CartStatus => LIST_STATUS_FILTERS.includes(value as CartStatus)) : DEFAULT_STATUS_FILTERS,
    sort: sort.length > 0 ? sort : ['n-to-o' as AccountDateSortType],
    dateRange: dateRange === 'all' ? [] : [dateRange],
    dateFrom: paramDateFrom ? [paramDateFrom] : [],
    dateTo: paramDateTo ? [paramDateTo] : [],
  })
  const defaultSearch = useMemo(() => ({
    search: [],
    statuses: DEFAULT_STATUS_FILTERS,
    sort: ['n-to-o' as AccountDateSortType],
    dateRange: [],
    dateFrom: [],
    dateTo: [],
  }), [])
  const [limit, setLimit] = useState(userLimit)
  const searchText = useMemo(() => {
    return search.search.join(',')
  }, [search.search])

  const loadingItems = useMemo(() => {
    const listSkeletons = []

    for (let i = 0; i < limit; i++) {
      listSkeletons.push(<OrderRowSkeleton key={i} index={i} />)
    }

    return listSkeletons
  }, [limit])

  const renderOrder = useCallback((item: UserCartStore, index: number) => {
    return <OrderRow
      order={item}
      index={index}
      detailUrl={tR('/account/orders/{{ref_id}}', {
        ref_id: item.get('ref_id')
      })}
    />
  }, [tR])

  const onChangeStatus = useCallback((statuses: CartStatus[]) => {
    setSearch(search => ({
      ...search,
      statuses,
    }))
  }, [])

  const onChangeSort = useCallback((nextSort: AccountDateSortType) => {
    setSearch(search => ({
      ...search,
      sort: [nextSort],
    }))
  }, [])

  const onChangeDateRange = useCallback((value: AccountDateRangeValue) => {
    setSearch(search => ({
      ...search,
      dateRange: value.preset === 'all' ? [] : [value.preset],
      dateFrom: value.dateFrom ? [value.dateFrom] : [],
      dateTo: value.dateTo ? [value.dateTo] : [],
    }))
  }, [])

  const onChangeSearchText = useCallback((value: string) => {
    setSearch(search => ({
      ...search,
      search: value === '' ? [] : value.split(',')
    }))
  }, [])

  const selectedSort = search.sort[0] ?? 'n-to-o'
  const selectedDateRange = (search.dateRange[0] as AccountDateRangePreset | undefined) ?? 'all'
  const dateRangeFilterActive = selectedDateRange !== 'all'
  const statusFilterActive = !isDefaultStatusFilter(search.statuses)
  const sortOptions: AccountSortOption<AccountDateSortType>[] = [
    { value: 'n-to-o', label: t('Newest first') },
    { value: 'o-to-n', label: t('Oldest first') },
  ]
  const dateRangeOptions = [
    { value: 'all' as AccountDateRangePreset, label: t('All time') },
    { value: 'today' as AccountDateRangePreset, label: t('Today') },
    { value: 'last-7-days' as AccountDateRangePreset, label: t('Last 7 days') },
    { value: 'last-30-days' as AccountDateRangePreset, label: t('Last 30 days') },
    { value: 'this-month' as AccountDateRangePreset, label: t('This month') },
    { value: 'last-month' as AccountDateRangePreset, label: t('Last month') },
    { value: 'custom' as AccountDateRangePreset, label: t('Custom') },
  ]

  const renderList = useCallback((items: JSX.Element | null, links: JSX.Element | null, loading: boolean) => {
    const hasRows = loading || !!items

    return <>
      <Stack>
        {hasRows ? <>
          <Box
            component="div"
            sx={{
              display: {
                xs: 'none',
                lg: 'block'
              }
            }}
          >
            <div>
              <Stack
                direction="row"
                spacing={4}
                sx={{
                  py: 2,
                  px: 2
                }}
              >
                <Box
                  component="div"
                  sx={{ width: '100px' }}
                >
                  <Typography variant="h4">{t('Order')}</Typography>
                </Box>
                <Box
                  component="div"
                  sx={{ width: '170px' }}
                >
                  <Typography variant="h4">{t('Date')}</Typography>
                </Box>
                <Box
                  component="div"
                  sx={{ flexGrow: 1 }}
                >
                  <Typography variant="h4">{t('Ship to')}</Typography>
                </Box>
                <Box
                  component="div"
                  sx={{ width: '130px' }}
                >
                  <Typography variant="h4">{t('Total')}</Typography>
                </Box>
                <Box
                  component="div"
                  sx={{
                    width: '150px',
                    display: {
                      md: 'none',
                      lg: 'block'
                    }
                  }}
                >
                  <Typography variant="h4">{t('Status')}</Typography>
                </Box>
              </Stack>
            </div>
            <Divider variant="bold" />
          </Box>
          <Stack
            spacing={{
              xs: 2,
              lg: 0
            }}
          >
            {loading ? loadingItems : items}
          </Stack>
        </> : <AccountNoResults embedded />}
      </Stack>

      {links}
    </>
  }, [t, loadingItems])

  return <div>
    <AccountHeader
      title={t('Orders')}
    />
    <SearchBar
      placeholder={t('Search by order number or name...')}
      value={searchText}
      onChange={onChangeSearchText}
    >
      <AccountFilterDrawer activeCount={(statusFilterActive ? 1 : 0) + (dateRangeFilterActive ? 1 : 0)}>
        <AccountFilterSection title={t('Filters')}>
          <AccountDateRangeFilter
            label={t('Order date')}
            value={{
              preset: selectedDateRange,
              dateFrom: search.dateFrom[0] ?? '',
              dateTo: search.dateTo[0] ?? '',
            }}
            options={dateRangeOptions}
            fromLabel={t('From')}
            toLabel={t('To')}
            onChange={onChangeDateRange}
          />
          <OrderStatusFilter
            label={t('Status')}
            value={search.statuses}
            statuses={LIST_STATUS_FILTERS}
            onChange={onChangeStatus}
          />
        </AccountFilterSection>
        <AccountFilterSection title={t('Display')}>
          <AccountSortFilter
            label={t('Sort by')}
            value={selectedSort}
            options={sortOptions}
            onChange={onChangeSort}
          />
          <AccountLimitFilter
            label={t('Results per page')}
            value={limit}
            onChange={setLimit}
          />
        </AccountFilterSection>
      </AccountFilterDrawer>
    </SearchBar>
    <Paper
      elevation={0}
      sx={{
        backgroundColor: {
          xs: 'transparent',
          lg: '#fff'
        }
      }}
    >
      <ItemList
        dataType="orders"
        item={renderOrder}
        searchQuery={search}
        defaultSearchQuery={defaultSearch}
        searchFields={SEARCH_FIELDS}
        limit={limit}
        render={renderList}
      />
    </Paper>
  </div>
}
